'use client';

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import {
  setGeneralSettings,
  setBusinessSettings,
  setSettingsLoading,
  setSettingsError,
} from '@/lib/redux/features/settingsSlice';
import { API_GENERAL_SETTINGS, API_BUSINESS_SETTINGS } from '@/constant/apiRout';
import type { RootState } from '@/lib/redux/store';

export const useSettings = () => {
  const dispatch = useDispatch();
  const { generalSettings, businessSettings, isLoading, error } = useSelector(
    (state: RootState) => state.settings
  );

  useEffect(() => {
    const fetchSettings = async () => {
      // Only fetch if settings are not already loaded
      if (generalSettings && businessSettings) {
        return;
      }

      dispatch(setSettingsLoading(true));
      dispatch(setSettingsError(null));

      try {
        const [generalRes, businessRes] = await Promise.all([
          axios.get(`${process.env.NEXT_PUBLIC_BACKEND_URL}${API_GENERAL_SETTINGS}`, {
            params: { tags: ['general-settings'] },
          }),
          axios.get(`${process.env.NEXT_PUBLIC_BACKEND_URL}${API_BUSINESS_SETTINGS}`, {
            params: { tags: ['business-settings'] },
          }),
        ]);

        dispatch(setGeneralSettings(generalRes.data?.data || null));
        dispatch(setBusinessSettings(businessRes.data?.data || null));
      } catch (err) {
        dispatch(setSettingsError((err as Error).message));
        console.error('Failed to fetch settings:', err);
      } finally {
        dispatch(setSettingsLoading(false));
      }
    };

    fetchSettings();
  }, [dispatch, generalSettings, businessSettings]);

  return {
    generalSettings,
    businessSettings,
    isLoading,
    error,
  };
};
